/**
 * Theme toggle button – sun/moon icon, toggles light/dark and persists preference.
 * Uses ThemeContext; same design on landing and in portal.
 */

import { EVENTS } from '@constants/telemetryEvents';
import { useTheme } from '@contexts/ThemeContext';
import { useTelemetry } from '@hooks/useTelemetry';

import './ThemeToggle.css';

export interface ThemeToggleProps {
  /** Optional class for the wrapper (e.g. for layout positioning) */
  className?: string;
  /** When true, hide on small screens (e.g. show in mobile menu instead) */
  hideOnMobile?: boolean;
}

export function ThemeToggle({ className, hideOnMobile }: ThemeToggleProps) {
  const { isDark, toggleTheme } = useTheme();
  const { track } = useTelemetry();
  const handleToggle = () => {
    toggleTheme();
    track(EVENTS.SETTINGS_THEME_TOGGLED, { theme: isDark ? 'light' : 'dark' });
  };

  return (
    <div
      className={`bhg-theme-toggle${className ? ` ${className}` : ''}${hideOnMobile ? ' bhg-theme-toggle--hide-mobile' : ''}`}
    >
      <button
        type="button"
        className="bhg-theme-toggle__btn"
        onClick={handleToggle}
        aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
      >
        <span className="bhg-theme-toggle__icons">
          <span className="bhg-theme-toggle__icons-inner">
            <span className="bhg-theme-toggle__icon" aria-hidden>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="5" />
                <path d="M12 1v2M12 21v2M4.22 4.22l1.42 1.42M18.36 18.36l1.42 1.42M1 12h2M21 12h2M4.22 19.78l1.42-1.42M18.36 5.64l1.42-1.42" />
              </svg>
            </span>
            <span className="bhg-theme-toggle__icon" aria-hidden>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
              </svg>
            </span>
          </span>
        </span>
      </button>
    </div>
  );
}
